/**
 * Hook for managing user folders
 *
 * Provides folder listing, creation, and management operations.
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, FolderResponse } from '../api';
import { queryKeys } from '../query-keys';

export interface UseFoldersOptions {
  /** Parent folder ID to list children of (null = root folders only, undefined = all folders) */
  parentFolderId?: string | null;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

export interface UseFoldersResult {
  /** List of folders */
  folders: FolderResponse[];
  /** Whether folders are loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Fetch folders */
  fetchFolders: () => Promise<void>;
  /** Create a new folder */
  createFolder: (name: string, parentFolderId?: string) => Promise<FolderResponse>;
  /** Rename a folder */
  renameFolder: (folderId: string, name: string) => Promise<FolderResponse>;
  /** Move a folder */
  moveFolder: (folderId: string, newParentFolderId: string | null) => Promise<FolderResponse>;
  /** Delete a folder */
  deleteFolder: (folderId: string, deleteContents?: boolean) => Promise<void>;
  /** Get folder breadcrumb */
  getBreadcrumb: (folderId: string) => Promise<FolderResponse[]>;
  /** Get folder by ID */
  getFolder: (folderId: string) => Promise<FolderResponse>;
  /** Refresh folder list */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing user folders
 */
export function useFolders(options: UseFoldersOptions = {}): UseFoldersResult {
  const { parentFolderId, autoFetch = true } = options;
  const queryClient = useQueryClient();

  // Keep local state for optimistic updates
  const [folders, setFolders] = useState<FolderResponse[]>([]);

  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.folders.list(parentFolderId),
    queryFn: () => api.listFolders(parentFolderId),
    enabled: autoFetch,
  });

  // Sync query data into local state
  useEffect(() => {
    if (data?.folders) {
      setFolders(data.folders);
    }
  }, [data]);

  const error =
    queryError instanceof Error
      ? queryError
      : queryError
        ? new Error('Failed to fetch folders')
        : null;

  const fetchFolders = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId?: string }) =>
      api.createFolder({ name, parentFolderId: parentId }),
    onSuccess: (response, { parentId }) => {
      // Add to local state if in same parent
      const isInSameParent = (parentId ?? null) === (parentFolderId ?? null);
      if (isInSameParent) {
        setFolders((prev) => [...prev, response.folder]);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
    },
  });

  const createFolder = useCallback(
    async (name: string, parentId?: string): Promise<FolderResponse> => {
      const response = await createFolderMutation.mutateAsync({ name, parentId });
      return response.folder;
    },
    [createFolderMutation],
  );

  // Rename folder mutation
  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      api.updateFolder(folderId, { name }),
    onSuccess: (response, { folderId }) => {
      setFolders((prev) => prev.map((f) => (f.folderId === folderId ? response.folder : f)));
    },
  });

  const renameFolder = useCallback(
    async (folderId: string, name: string): Promise<FolderResponse> => {
      const response = await renameFolderMutation.mutateAsync({ folderId, name });
      return response.folder;
    },
    [renameFolderMutation],
  );

  // Move folder mutation
  const moveFolderMutation = useMutation({
    mutationFn: ({
      folderId,
      newParentFolderId,
    }: {
      folderId: string;
      newParentFolderId: string | null;
    }) => api.updateFolder(folderId, { parentFolderId: newParentFolderId }),
    onSuccess: (response, { folderId, newParentFolderId }) => {
      const movedToDifferentParent = (newParentFolderId ?? null) !== (parentFolderId ?? null);
      if (movedToDifferentParent) {
        setFolders((prev) => prev.filter((f) => f.folderId !== folderId));
      } else {
        setFolders((prev) => prev.map((f) => (f.folderId === folderId ? response.folder : f)));
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
    },
  });

  const moveFolder = useCallback(
    async (folderId: string, newParentFolderId: string | null): Promise<FolderResponse> => {
      const response = await moveFolderMutation.mutateAsync({ folderId, newParentFolderId });
      return response.folder;
    },
    [moveFolderMutation],
  );

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: ({ folderId, deleteContents }: { folderId: string; deleteContents: boolean }) =>
      api.deleteFolder(folderId, deleteContents),
    onSuccess: (_data, { folderId }) => {
      setFolders((prev) => prev.filter((f) => f.folderId !== folderId));
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
    },
  });

  const deleteFolder = useCallback(
    async (folderId: string, deleteContents: boolean = false): Promise<void> => {
      await deleteFolderMutation.mutateAsync({ folderId, deleteContents });
    },
    [deleteFolderMutation],
  );

  const getBreadcrumb = useCallback(async (folderId: string): Promise<FolderResponse[]> => {
    const response = await api.getFolderBreadcrumb(folderId);
    return response.breadcrumb;
  }, []);

  const getFolder = useCallback(async (folderId: string): Promise<FolderResponse> => {
    const response = await api.getFolder(folderId);
    return response.folder;
  }, []);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    folders,
    isLoading,
    error,
    fetchFolders,
    createFolder,
    renameFolder,
    moveFolder,
    deleteFolder,
    getBreadcrumb,
    getFolder,
    refresh,
  };
}

export default useFolders;
